import { Link } from "@inertiajs/react"
import { Music2 } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { onboardingTiktokPath } from "@/routes"

/**
 * TiktokParticipationGate — OKS-376.
 *
 * The single mandatory TikTok touchpoint surfaced in the UI: when an
 * unconnected creator (under the relaxation flag) hits a participate
 * affordance (`accept` / `apply` / `submit` / `mark_posted`), the live
 * CTA is REPLACED by this gate, routing into the (now optional) connect
 * flow. Browsing / view-only CTAs are never gated — only participation.
 *
 * The server-side gate (`Submissions::Create` → `:tiktok_auth_revoked`)
 * is the real defense; this is the matching UX affordance so the creator
 * never hits a bare error.
 *
 * Two render modes:
 *   - `variant="card"` (default): a full-width secondary pill in the
 *     collab-card CTA slot (mirrors `<CTAButton>`'s 44px pill).
 *   - `variant="detail"`: an informational `Alert` for the mission
 *     detail surface, with a title + body + primary connect button.
 *
 * Accessibility: Music2 icon + text (color is never the sole signal);
 * the connect target is a real `<a>` (Inertia `<Link>`), full nav, no
 * modal / focus-trap.
 */
interface TiktokParticipationGateProps {
  variant?: "card" | "detail"
  /** Disambiguates multiple gates on one screen (e.g. card lists). */
  testIdSuffix?: string | number
  className?: string
}

export function TiktokParticipationGate({
  variant = "card",
  testIdSuffix,
  className,
}: TiktokParticipationGateProps) {
  const { t } = useTranslation()
  const testId =
    testIdSuffix === undefined
      ? "tiktok-participation-gate"
      : `tiktok-participation-gate-${testIdSuffix}`

  if (variant === "detail") {
    return (
      <Alert role="status" data-testid={testId} className={className}>
        <Music2 aria-hidden="true" />
        <AlertTitle>{t("tiktok_optional.gate.detail_title")}</AlertTitle>
        <AlertDescription className="flex flex-col items-start gap-3">
          <span>{t("tiktok_optional.gate.detail_body")}</span>
          <Button asChild size="sm">
            <Link href={onboardingTiktokPath()}>
              {t("tiktok_optional.gate.connect")}
            </Link>
          </Button>
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <Button
      asChild
      variant="secondary"
      className={className ?? "h-11 w-full"}
      data-testid={testId}
    >
      <Link href={onboardingTiktokPath()}>
        <Music2 aria-hidden="true" />
        {t("tiktok_optional.gate.card_label")}
      </Link>
    </Button>
  )
}
